import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

/**
 * ProtectedRoute component that guards routes requiring authentication
 * Prevents access via browser back button after logout by validating token
 */
function ProtectedRoute({ children, adminOnly = false }) {
  const location = useLocation();
  const [isValidating, setIsValidating] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  const validateAuth = React.useCallback(async () => {
    const token = localStorage.getItem('token');
    const storedIsAdmin = localStorage.getItem('isAdmin') === 'true';

    // Only log in development
    if (process.env.NODE_ENV === 'development') {
      console.log('[ProtectedRoute] Validating auth...', { 
        hasToken: !!token, 
        storedIsAdmin, 
        adminOnly 
      });
    }

    if (!token) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[ProtectedRoute] No token found, redirecting to login');
      }
      setIsAuthenticated(false);
      setIsValidating(false);
      return;
    }

    try {
      // Validate token with backend
      const response = await fetch(`${process.env.REACT_APP_API}/api/auth/verify`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });

      if (response.ok) {
        const data = await response.json();
        const userIsAdmin = data.user?.isAdmin || false;
        
        if (process.env.NODE_ENV === 'development') {
          console.log('[ProtectedRoute] Token validated', { 
            userIsAdmin, 
            storedIsAdmin,
            userId: data.user?.id 
          });
        }
        
        setIsAuthenticated(true);
        setIsAdmin(userIsAdmin);
        
        // Update localStorage if there's a mismatch
        if (String(userIsAdmin) !== localStorage.getItem('isAdmin')) {
          if (process.env.NODE_ENV === 'development') {
            console.log('[ProtectedRoute] Updating isAdmin in localStorage');
          }
          localStorage.setItem('isAdmin', String(userIsAdmin));
        }
      } else {
        if (process.env.NODE_ENV === 'development') {
          console.error('[ProtectedRoute] Token validation failed', response.status);
        }
        // Token invalid - clear storage
        localStorage.removeItem('token');
        localStorage.removeItem('isAdmin');
        setIsAuthenticated(false);
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('[ProtectedRoute] Auth validation error:', error);
      }
      // On network error, do a basic check
      setIsAuthenticated(!!token);
      setIsAdmin(storedIsAdmin);
    }

    setIsValidating(false);
  }, [adminOnly]);

  useEffect(() => {
    // Prevent page caching - critical for security
    window.history.replaceState(null, '', window.location.href);
    
    const handleNavigation = () => {
      // Re-validate auth on browser back/forward
      validateAuth();
    };

    window.addEventListener('popstate', handleNavigation);
    return () => window.removeEventListener('popstate', handleNavigation);
  }, [validateAuth]);

  useEffect(() => {
    validateAuth();
  }, [location.pathname, validateAuth]);

  // Show loading state while validating
  // Show loading state while validating - return null to avoid loading flash
  if (isValidating) {
    return null;
  }

  // Not authenticated - redirect to login
  if (!isAuthenticated) {
    if (process.env.NODE_ENV === 'development') {
      console.log('[ProtectedRoute] Not authenticated, redirecting to login');
    }
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // Admin route but user is not admin
  if (adminOnly && !isAdmin) {
    if (process.env.NODE_ENV === 'development') {
      console.log('[ProtectedRoute] Admin route but user is not admin, redirecting to home');
    }
    return <Navigate to="/home" replace />;
  }

  if (process.env.NODE_ENV === 'development') {
    console.log('[ProtectedRoute] All checks passed, rendering protected content');
  }
  // All checks passed - render protected content
  return children;
}

export default ProtectedRoute;
